import React, { useContext, useEffect, useState } from 'react'
import {
    Typography,
    TextField,
    Stack,
    Box,
    CircularProgress,
    Button,
    Switch,
    Alert,
    Snackbar,
    Divider,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Card,
    Select,
    Modal,
    Paper
} from '@mui/material'
import { VpnKeyOutlined, DeleteOutlined, MailOutline } from '@mui/icons-material'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import { ROLE_MAPPINGS, 
    PART_FULLTIME_MAPPINGS, 
    OPEN_CLOSE_MAPPINGS
 } from '../../constants'
import ButtonWithConfirmPrompt from '../ButtonWithConfirmPrompt'
import { PATHS } from '../../constants'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import SubmitSvg from '../SubmitShift/SubmitSvg'
import { isMobile } from '../../Utilities/isMobile'
import { useParams } from 'react-router-dom'
import { validateHiraganaKatakana, 
    validateKanjiHiraganaKatakana, 
    validateEmail, 
    validatePhone,
    validateEmpCode 
} from '../../Utilities/fieldValidators'
import EditStores from './EditFlows/EditStores'

const UserManagementEdit = ({ history, setSelectedEmployment }) => {
    const { userEmpId, userId } = useParams()
    const employmentId = localStorage.getItem('selected_emp')
    const { isAdmin, fetchAndSetEmployments } = useContext(EmploymentsContext)
    const [info, setInfo] = useState()
    const [originalInfo, setOriginalInfo] = useState()
    const [isTargetUserManager, setIsTargetUserManager] = useState()
    const [employments, setEmployments] = useState()
    const [originalEmployments, setOriginalEmployments] = useState()
    const [editing, setEditing] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [errors, setErrors] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [newPass, setNewPass] = useState(null)
    const [storeList, setStoreList] = useState([])
    const [openEditStores, setOpenEditStores] = useState(false)
    const fetch = async () => {
        const userInfo = await AxiosWithAuth.get('/users/' + userId)
        const employmentInfo = userEmpId !== 'noEmployment' ? await AxiosWithAuth.get('/employments/' + userEmpId) : {}
        userInfo.data.employments.length && (userInfo.data.store_ids = userInfo.data.employments.map(e => e.store_id))
        const userEmploymentsInfo = await AxiosWithAuth.get('/employments/?user_id=' + userId)
        await AxiosWithAuth.get('/stores').then(res => setStoreList(res.data))
        setEmployments(userEmploymentsInfo.data?.sort((a, b) => a.id - b.id))
        setOriginalEmployments(userEmploymentsInfo.data?.sort((a, b) => a.id - b.id))
        const infoToAdd = userEmpId === 'noEmployment' ? userEmploymentsInfo?.data[0] : employmentInfo.data
        setInfo({ ...infoToAdd, ...userInfo.data })
        setOriginalInfo({ ...infoToAdd, ...userInfo.data })
        setIsTargetUserManager(userEmploymentsInfo.data.some(e => e.is_manager))
    }

    const handleSave = () => {
        if (Object.values(errors).some(e => e)) {
            return
        }
        employments.forEach((e, i) => {
            const originalEmployment = originalEmployments[i]
            const updatedEmployment = Object.keys(e).reduce((acc, key) => {
                if (e[key] !== originalEmployment[key]) {
                    acc[key] = e[key]
                }
                return acc
            }, {})
            if (Object.keys(updatedEmployment).length) {
                AxiosWithAuth.put('/employments/' + e.id, {
                    ...updatedEmployment
                })
                    .then(res => {
                        res.status === 200 && setSnackbarMessage('変更を保存しました。')
                    })
                    .catch(error => {
                        const errors = {}
                        for (const [key, value] of Object.entries(error?.response?.data)) {
                            errors[key] = value.join(' ')
                        }
                        setErrors(errors)
                    })
            }
        })
        const updatedInfo = Object.keys(info).reduce((acc, key) => {
            if (info[key] !== originalInfo[key]) {
                acc[key] = info[key]
            }
            return acc
        }, {})
        if (Object.keys(updatedInfo).length) {
            AxiosWithAuth.put('/users/' + userId, {
                ...updatedInfo
            })
                .then(res => {
                    res.status === 200 && handleReturn()
                })
                .catch(error => {
                    const errors = {}
                    for (const [key, value] of Object.entries(error?.response?.data)) {
                        errors[key] = value.join(' ')
                    }
                    setErrors(errors)
                })
        }
    }

    const handleReturn = () => {
        history.push(PATHS.managerSettingsUsers)
        setSelectedEmployment(null)
    }

    useEffect(() => {
        if (info && originalInfo) {
            const updated = Object.keys(info).some(key => info[key] !== originalInfo[key])
            const updatedEmployments = employments.some((e, i) => {
                const originalEmployment = originalEmployments[i]
                return Object.keys(e).some(key => e[key] !== originalEmployment[key])
            })
            if (updated || updatedEmployments) {
                setEditing(true)
            } else {
                setEditing(false)
            }
        }
    }, [info, originalInfo, employments, originalEmployments])

    useEffect(() => {
        fetch()
    }, [])

    if (!info) {
        return <CircularProgress sx={{ mx: '50%', mt: '10%' }} />
    }

    if (newPass)
        return (
            <div
                style={{
                    height: 'calc(100vh - 56px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: '0 1rem'
                }}
            >
                <SubmitSvg />
                <Typography variant="header1" sx={{ fontSize: '20px' }}>
                    新しいパスワード: {newPass}
                </Typography>
                <Button
                    onClick={() => {
                        setNewPass(null)
                    }}
                    variant="contained"
                    color="paloBlue"
                    fullWidth
                    sx={{ mt: 'auto', mb: '2rem' }}
                >
                    完了
                </Button>
            </div>
        )
    const getTextField = (label, key, disable=false) => {
        const validators = {
            name_last: validateHiraganaKatakana,
            name_first: validateHiraganaKatakana,
            kanji_last: validateKanjiHiraganaKatakana,
            kanji_first: validateKanjiHiraganaKatakana,
            name_middle: validateHiraganaKatakana,
            nickname: validateHiraganaKatakana,
            email: validateEmail,
            phone: validatePhone,
            emp_cd: validateEmpCode,
            hourly_wage: validateEmpCode
        }
        const errorText = {
            name_last: '全角ひらがな・全角カタカナ',
            name_first: '全角ひらがな・全角カタカナ',
            kanji_last: '全角ひらがな・全角カタカナ',
            kanji_first: '全角ひらがな・全角カタカナ',
            name_middle: '全角ひらがな・全角カタカナ',
            nickname: '全角ひらがな・全角カタカナ',
            email: '半角英数字',
            phone: '半角数字',
            emp_cd: '半角数字',
            hourly_wage: '半角数字'
        }
        return <TextField
            label={label}
            value={info[key]}
            onChange={e => {
                if (!validators[key](e.target.value)) {
                    setErrors({ ...errors, [key]: errorText[key] })
                } else {
                    setErrors({ ...errors, [key]: '' })
                }
                setInfo({ ...info, [key]: e.target.value })            
            }}
            sx={{ width: isMobile() ? '90%' : '50%' }}
            error={errors[key]}
            helperText={errors[key]}
            disabled={disable}
            size={isMobile() ? 'small' : 'medium'}
        />
    }

    const getStoreTextField = (label, key) => {
        const selectedStores = storeList.filter(store => info[key]?.includes(store.id))
        return <TextField
            label={label}
            value={selectedStores.map(store => store.name).join(', ')}
            onClick={() => {
                setOpenEditStores(true)
            }}
            sx={{ width: isMobile() ? '90%' : '50%' }}
        />
    }

    const getNumberField = (label, key, disable=false) => {
        return <TextField
            label={label}
            value={info[key]}
            type="number"
            sx={{ width: '50%' }}
            onChange={e => {
                setInfo({ ...info, [key]: e.target.value })
            }}
            disabled={disable}
        />
    }
    
    const AdminManagerButtons = () => {
        return <>
            {!info.confirmed_at && (
                <Stack direction="row" spacing={2}>
                    <ButtonWithConfirmPrompt
                        component={
                            <Button variant="outlined" color="paloBlue" sx={{ mx: '1rem', mb: '1rem' }}>
                                <MailOutline sx={{ mr: '.5rem' }} />
                                メールアドレスを代理で確認
                            </Button>
                        }
                        title="メールアドレスを代理で確認"
                        content="このユーザーのメールアドレスを代理で確認しますか？"
                        confirmText="確認する"
                        handleConfirm={() => {
                            AxiosWithAuth.post('/confirmUser/', { id: info.id }).then(res => {
                                console.log(res.data)
                                if (res.status == 200) {
                                    setSnackbarMessage('メールアドレスの本人確認が完了しました。')
                                    fetch()
                                }
                            })
                        }}
                    />
                </Stack>
            )}
            <Stack direction="row" spacing={2}>
                <ButtonWithConfirmPrompt
                    component={
                        <Button variant="outlined" color="paloBlue" sx={{ mx: '1rem', mb: '1rem' }}>
                            <VpnKeyOutlined sx={{ mr: '.5rem' }} />
                            このユーザーのパスワードをリセット
                        </Button>
                    }
                    title="パスワードのリセット"
                    content="このユーザーのパスワードをリセットしますか？"
                    confirmText="リセットする"
                    handleConfirm={() => {
                        AxiosWithAuth.post('/resetPassword', { id: info.id }).then(res => {
                            if (res.status == 200) {
                                setNewPass(res.data.password)
                            }
                        })
                    }}
                />
            </Stack>
            <Stack direction="row" spacing={2}>
                <ButtonWithConfirmPrompt
                    component={
                        <Button variant="outlined" color="error" sx={{ mx: '1rem', mb: '2rem' }}>
                            <DeleteOutlined sx={{ mr: '.5rem' }} />
                            このユーザーのアカウントを無効にする
                        </Button>
                    }
                    title="ユーザーの無効化"
                    content="アカウントを無効にすると、このユーザーはログインできなくなります。このユーザーを無効にしてもよろしいですか？"
                    confirmText="無効にする"
                    handleConfirm={() => {
                        AxiosWithAuth.post('/users/deactivate/' + userId).then(res => {
                            res.status == 200 && res.data.message === "User deactivated" && (window.location.pathname = 'managerSettings/userManagement/' + employmentId)
                        })
                    }}
                />
            </Stack>    
            {isAdmin && (
                <Stack direction="row" spacing={2}>
                    <ButtonWithConfirmPrompt
                        component={
                            <Button variant="outlined" color="error" sx={{ mx: '1rem', mb: '5rem' }}>
                                <DeleteOutlined sx={{ mr: '.5rem' }} />
                                このユーザーのアカウントを削除
                            </Button>
                        }
                        title="ユーザーの削除"
                        content="アカウントを削除すると過去のデータはすべて削除され、復元することはできません。このユーザーのアカウントを完全に削除してもよろしいですか？"
                        confirmText="削除する"
                        handleConfirm={() => {
                            setIsLoading(true)// Get the previous location from the history stack
                            const currPath = history.location.pathname
                            const newPath = currPath.includes('admin') ? PATHS.adminUserManagements : PATHS.userManagementStores + employmentId + '/deleted'

                            AxiosWithAuth.delete('/users/' + userId).then(res => {
                                res.status == 204 &&
                                    history.push({ pathname: newPath, state: { deleted: userId } })
                                setIsLoading(false)
                            })
                        }}
                    />
                </Stack>
            )}
        </>
    }
    return (
        <div style={{ marginTop: '-2rem'}}>
            <Box className="input-box shrinkable">
                <Snackbar
                    open={snackbarMessage !== ''}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarMessage('')}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbarMessage('')}>{snackbarMessage}</Alert>
                </Snackbar>
            </Box>
            {!info.confirmed_at && <Alert severity="error">メールアドレスの本人確認が未完了です。</Alert>}
            <Stack direction={isMobile() ? "column" : "row"} spacing={2} sx={{ mt: '1rem' }}>
                <Card sx={{ p: isMobile() ? 4 : 2, width: isMobile() ? '90vw' : '48%', boxShadow: isMobile() && 'none'}} >
                    <Typography variant="h6" sx={{ mb: 2 }}>基本情報</Typography>
                    <Stack spacing={2} sx={{ flexWrap: 'wrap'}}>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('せい(かな)', 'name_last')}
                            {getTextField('めい(かな)', 'name_first')}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('姓(漢字)', 'kanji_last')}
                            {getTextField('名(漢字)', 'kanji_first')}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('ミドルネーム', 'name_middle')}
                            {getTextField('ニックネーム', 'nickname')}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('メールアドレス', 'email')}
                            {getTextField('電話番号', 'phone')}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getStoreTextField('店舗情報', 'store_ids')} 
                            {getTextField('社員コード', 'emp_cd')}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            <Select
                                id="eType"
                                label="雇用形態"
                                placeholder="選択してください"
                                value={info.e_type || ''}
                                onChange={event =>
                                    (isTargetUserManager || isAdmin) && setInfo({ ...info, e_type: event.target.value })
                                }
                                sx={{ width: '49%' }}
                                disabled={!isTargetUserManager && !isAdmin}
                            >
                                {Object.keys(PART_FULLTIME_MAPPINGS).map(key => (
                                    <MenuItem key={key} value={key}>
                                        {PART_FULLTIME_MAPPINGS[key]}
                                    </MenuItem>
                                ))}
                            </Select>
                            {getTextField('時給', 'hourly_wage', !isTargetUserManager && !isAdmin)}
                            
                        </Stack>
                        {getNumberField('最大労働時間', 'monthly_max_hours', !isTargetUserManager && !isAdmin)} 
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        style={{ marginLeft: '-0.75rem' }}
                                        checked={info.view_only}
                                        onChange={e => setInfo({ ...info, view_only: e.target.checked })}
                                    />
                                }
                                label="正社員(間接)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        style={{ marginLeft: '-0.75rem' }}
                                        checked={info.is_student}
                                        onChange={e => setInfo({ ...info, is_student: e.target.checked })}
                                    />
                                }
                                label="高校生"
                            />
                        </Stack>
                        {!isMobile() && <AdminManagerButtons />}
                    </Stack>
                </Card>
                <Box style={{ width: isMobile() ? '90vw' : '45%', marginBottom: isMobile() && '4rem' }}>
                    {employments.length ? employments.map(e => {
                        return (
                            <Card key={e.id} sx={{ p: isMobile() ? 3 : 2, width: isMobile() ? '90vw' : '100%', boxShadow: isMobile() && 'none'}} >
                                <Typography variant="header1" sx={{ fontSize: '20px' }}>
                                    {e.store.name}
                                </Typography>
                                <Divider sx={{ marginTop: '.5rem' }} />
                                <Stack spacing={2} margin="1rem">
                                    <TextField
                                        select
                                        id="kitchen/hall"
                                        label="勤務タイプ"
                                        variant="standard"
                                        placeholder="選択してください"
                                        value={e.role || ''}
                                        onChange={event =>
                                            setEmployments(
                                                employments.map(emp =>
                                                    emp.id === e.id ? { ...emp, role: event.target.value } : emp
                                                )
                                            )
                                        }
                                    >
                                        {Object.keys(ROLE_MAPPINGS).map(key => (
                                            <MenuItem key={key} value={key}>
                                                {ROLE_MAPPINGS[key]}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        id="open/close"
                                        label="開閉担当者"
                                        variant="standard"
                                        placeholder="選択してください"
                                        value={e.open_close_duty || ''}
                                        onChange={event =>
                                            setEmployments(
                                                employments.map(emp =>
                                                    emp.id === e.id ? { ...emp, open_close_duty: event.target.value } : emp
                                                )
                                            )
                                        }
                                    >
                                        {Object.keys(OPEN_CLOSE_MAPPINGS).map(key => (
                                            <MenuItem key={key} value={key}>
                                                {' '}
                                                {OPEN_CLOSE_MAPPINGS[key]}{' '}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        id="rank"
                                        label="ランク"
                                        variant="standard"
                                        placeholder="選択してください"
                                        value={e.rank || ''}
                                        onChange={event =>
                                            setEmployments(
                                                employments.map(emp =>
                                                    emp.id === e.id ? { ...emp, rank: event.target.value } : emp
                                                )
                                            )
                                        }
                                    >
                                        {['A', 'B', 'T'].map(key => (
                                            <MenuItem key={key} value={key}>
                                                {key}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        margin: '0 1rem'
                                    }}
                                >
                                    <Typography variant="subtitle1">シフト更新権限</Typography>
                                    <Switch
                                        checked={e.can_edit_shift}
                                        onChange={event => {
                                            if (e.support_staff) {
                                                setSnackbarMessage('サポートスタッフはシフト更新権限を持つことができません。')
                                                return
                                            }
                                            setEmployments(
                                                employments.map(emp =>
                                                    emp.id === e.id ? { ...emp, can_edit_shift: event.target.checked } : emp
                                                )
                                            )
                                        }}
                                    />
                                </div>
                                <Typography
                                    variant="body2"
                                    style={{
                                        margin: '0 1rem',
                                        marginBottom: '1rem',
                                        marginRight: '2rem',
                                        color: 'grey'
                                    }}
                                >
                                    シフト更新権限があるユーザーはシフトを作成することができます。
                                </Typography>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        margin: '0 1rem'
                                    }}
                                >
                                    <Typography variant="subtitle1">マネージャー</Typography>
                                    <Switch
                                        checked={e.is_manager}
                                        disabled={info.e_type != 'full-time'}
                                        onChange={event => {
                                            if (e.support_staff) {
                                                setSnackbarMessage('サポートスタッフはマネージャーにできません。')
                                                return
                                            }
                                            setEmployments(
                                                employments.map(emp =>
                                                    emp.id === e.id ? { ...emp, is_manager: event.target.checked } : emp
                                                )
                                            )
                                        }}
                                    />
                                </div>
                                <Typography
                                    variant="body2"
                                    style={{
                                        margin: '0 1rem',
                                        marginBottom: '1rem',
                                        marginRight: '2rem',
                                        color: 'grey'
                                    }}
                                >
                                    正社員のみマネージャーにできます。
                                </Typography>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        margin: '0 1rem'
                                    }}
                                >
                                    <Typography variant="subtitle1">サポートスタッフ</Typography>
                                    <Switch
                                        checked={e.support_staff}
                                        onChange={event => {
                                            const checked = event.target.checked
                                            setEmployments(
                                                employments.map(emp =>
                                                    emp.id === e.id ? { ...emp, support_staff: checked, 
                                                                                is_manager: checked ? false : emp.is_manager, 
                                                                                can_edit_shift: checked ? false : emp.can_edit_shift } : emp
                                                )
                                            )
                                        }}
                                    />
                                </div>
                            </Card>
                        )
                    }) : <Typography>所属店舗情報がありません。</Typography>}
                </Box>
            </Stack>
            <Box style={{ backgroundColor: 'white', width: '100%', position: 'fixed', bottom: 0, marginLeft: '-1rem', zIndex: isMobile() && 1001, marginBottom: isMobile() && '3rem' }}>
                <Divider />
                <Box style={{ display: 'flex', justifyContent: 'left', margin: '1rem' }}>
                    <Button variant='contained' sx={{ ml: 2 }} onClick={handleSave} disabled={!editing}>
                        変更して保存する
                    </Button>
                    <Button variant='outlined' sx={{ ml: 2 }} onClick={handleReturn}>
                        キャンセル
                    </Button>
                </Box>
                {isLoading && <CircularProgress sx={{ mx: '50%', mt: '10%' }} />}
                {openEditStores && <Modal open={openEditStores} onClose={() => setOpenEditStores(false)}>
                    <Paper sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90vw',
                            p: 2,
                            borderRadius: '10px',
                            height: 'auto',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                    }}>
                        <EditStores
                            current={employments}
                            handleReturn={() => {fetch(); setOpenEditStores(false)}}
                            employmentInfo={info}
                            fetchAndSetEmployments={fetchAndSetEmployments}
                            history={history}
                            titleOnly={true}
                        />
                    </Paper>
                </Modal>}
            </Box>
        </div>
    )
}

export default UserManagementEdit
